<template>
  <!--
    `data-xp` is the whole Explain switch. One attribute on the root turns on the
    dotted underline and the `?` badge for every [data-help] element below, via
    CSS alone — no per-label component, and nothing rendered at all when it is off.
  -->
  <div ref="root" class="atp2" :data-xp="explain ? '1' : null">

    <!-- ================================================================== -->
    <!-- HEADER                                                             -->
    <!-- ================================================================== -->
    <header class="atp2-header">
      <div style="min-width:0">
        <h1>Treatment presented vs accepted</h1>
      </div>

      <span class="atp2-range-pill">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><rect x="3" y="4.5" width="18" height="16.5" rx="2.5" /><path d="M3 10h18M8 2.5v4M16 2.5v4" /></svg>
        {{ rangeLabel }}
      </span>
      <!--
        Print only. On screen this is all visible in the filter controls, which
        don't print — so without it a PDF gives no clue what it was filtered to.
      -->

      <div class="atp2-header-actions atp2-noprint">
        <button
          type="button"
          class="atp2-btn atp2-xp-toggle"
          :class="{ 'is-on': explain }"
          :aria-pressed="explain ? 'true' : 'false'"
          title="Explain the numbers"
          @click="toggleExplain">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.6h.01" /></svg>
          Explain
          <span class="atp2-xp-track"><span class="atp2-xp-knob" /></span>
        </button>
        <button type="button" class="atp2-btn" @click="printReport">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V3h12v6" /><rect x="3" y="9" width="18" height="8" rx="2" /><path d="M6 17h12v4H6z" /></svg>
          Print / PDF
        </button>
        <button type="button" class="atp2-btn atp2-btn--ghost" title="Close" @click="closeSlideOutPanel">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
    </header>

    <div class="atp2-print-meta">
      <span><strong>Range</strong>{{ rangeLabel }}</span>
      <span><strong>Providers</strong>{{ printProviders }}</span>
      <span><strong>Procedures</strong>{{ printProcedures }}</span>
      <span><strong>Priorities</strong>{{ printPriorities }}</span>
      <span class="atp2-print-generated">Generated {{ printedAt }}</span>
    </div>

    <!--
      Shown the first few times Explain is switched on, then dismissed for good.
      Without it the dotted underlines read as a rendering glitch rather than an
      invitation.
    -->
    <div v-if="explain && showExplainCoach" class="atp2-xp-coach atp2-noprint">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.6h.01" /></svg>
      <span>Explain is on. Every label carrying a small mark has a plain-English definition — hover it, or tap on a touch screen.</span>
      <button type="button" class="atp2-xp-coach-got" @click="dismissExplainCoach">Got it</button>
    </div>

    <PopupErrorDisplay />
    <AdvancedTxPlannedV2Skeleton v-if="busy" />

    <template v-else>
      <!-- ================================================================ -->
      <!-- FILTERS                                                          -->
      <!-- ================================================================ -->
      <div class="atp2-filters atp2-noprint">

        <!-- ============================================================== -->
        <!-- QUICK SETS                                                     -->
        <!--                                                                -->
        <!-- Site-wide bundles of procedure codes. Comparing two hygienists  -->
        <!-- means hand-picking five codes out of ~1,200, every visit, by    -->
        <!-- every person who opens this report. One click instead.          -->
        <!-- ============================================================== -->
        <div class="atp2-quicksets">
          <span class="atp2-timeframe-label" data-help="quickSets">Quick sets</span>

          <button
            v-for="chip in quickSetChips"
            :key="chip.id"
            type="button"
            class="atp2-chip atp2-quickset-chip"
            :class="{ 'is-active': chip.active, 'is-suggested': chip.suggested, 'is-broken': chip.disabled }"
            :disabled="fetching || chip.disabled"
            :title="chip.title"
            @click="onQuickSetClick(chip)">
            {{ chip.name }}
            <span class="atp2-chip-count">{{ chip.count }}</span>
          </button>

          <span v-if="!quickSetChips.length" class="atp2-quickset-note">
            {{ quickSetsCorrupt
              ? 'This office has quick sets saved, but they could not be read.'
              : 'Save the codes you filter by most often so nobody has to re-pick them.' }}
          </span>

          <div class="atp2-quickset-actions">
            <button
              v-if="canManageQuickSets"
              type="button"
              class="atp2-btn atp2-btn--link"
              @click="openQuickSetPanel(true)">
              + Save current
            </button>
            <button
              v-if="canManageQuickSets"
              type="button"
              class="atp2-btn atp2-btn--ghost atp2-quickset-gear"
              title="Manage quick sets"
              @click="openQuickSetPanel(false)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3.2" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.11-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
            </button>

            <!-- ========================================================== -->
            <!-- MANAGE POPOVER                                             -->
            <!--                                                            -->
            <!-- Deliberately NOT a b-modal: the app's global               -->
            <!-- `.modal { overflow: visible }` breaks scrolling inside     -->
            <!-- modals, and this panel already wrestles the slideout host  -->
            <!-- for print layout. A plain absolutely-positioned card owned  -->
            <!-- by .atp2 avoids both.                                      -->
            <!--                                                            -->
            <!-- Edits accumulate in quickSetDraft and are written ONCE, so  -->
            <!-- Cancel really cancels and there is one round-trip.          -->
            <!-- ========================================================== -->
            <div v-if="quickSetPanelOpen" class="atp2-quickset-backdrop" @click="closeQuickSetPanel" />
            <div v-if="quickSetPanelOpen" class="atp2-quickset-panel">
              <div class="atp2-quickset-panel-head">
                <strong>Quick sets</strong>
                <span>Shared with everyone at this office</span>
              </div>

              <div class="atp2-quickset-new">
                <input
                  ref="quickSetName"
                  v-model="quickSetName"
                  type="text"
                  class="atp2-quickset-input"
                  :maxlength="MAX_NAME_LENGTH"
                  placeholder="Name this set"
                  @keydown.enter.prevent="addQuickSetFromSelection">
                <button
                  type="button"
                  class="atp2-btn atp2-btn--primary atp2-btn--sm"
                  :disabled="!draft.procCodes.length"
                  @click="addQuickSetFromSelection">
                  Add
                </button>
              </div>
              <p class="atp2-quickset-panel-hint">
                {{ draft.procCodes.length
                  ? 'From the ' + draft.procCodes.length + ' code(s) selected right now: ' + selectedCodeList
                  : 'Pick some procedure codes first — a set with no codes would never select anything.' }}
              </p>

              <ul v-if="quickSetDraft.length" class="atp2-quickset-list">
                <li v-for="(set, i) in quickSetDraft" :key="set.id">
                  <input
                    v-model="set.name"
                    type="text"
                    class="atp2-quickset-input"
                    :maxlength="MAX_NAME_LENGTH">
                  <span class="atp2-quickset-count">{{ set.codes.length }} codes</span>
                  <button
                    type="button"
                    class="atp2-btn atp2-btn--ghost atp2-btn--sm"
                    :disabled="!draft.procCodes.length"
                    :title="draft.procCodes.length
                      ? 'Replace with the ' + draft.procCodes.length + ' code(s) selected now'
                      : 'Select some codes first'"
                    @click="replaceQuickSetCodes(i)">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11A8 8 0 0 0 6.3 5.7L2 10" /><path d="M2 4v6h6" /><path d="M4 13a8 8 0 0 0 13.7 5.3L22 14" /><path d="M22 20v-6h-6" /></svg>
                  </button>
                  <button
                    type="button"
                    class="atp2-btn atp2-btn--ghost atp2-btn--sm atp2-quickset-del"
                    title="Remove this set"
                    @click="quickSetDraft.splice(i, 1)">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></svg>
                  </button>
                </li>
              </ul>
              <p v-else class="atp2-quickset-panel-hint">No quick sets yet.</p>

              <label v-if="quickSetsCorrupt" class="atp2-quickset-confirm">
                <input v-model="quickSetReplaceCorrupt" type="checkbox">
                The saved quick sets could not be read. Replace them.
              </label>

              <p v-if="quickSetError" class="atp2-quickset-error">{{ quickSetError }}</p>

              <div class="atp2-quickset-panel-foot">
                <button type="button" class="atp2-btn atp2-btn--link" @click="closeQuickSetPanel">Cancel</button>
                <button
                  type="button"
                  class="atp2-btn atp2-btn--primary atp2-btn--sm"
                  :disabled="quickSetSaving || !quickSetDirty"
                  @click="saveQuickSets">
                  {{ quickSetSaving ? 'Saving…' : 'Save sets' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p v-if="quickSetHint" class="atp2-quickset-hint">{{ quickSetHint }}</p>

        <div class="atp2-filter-grid">

          <!-- Procedure type OR procedure code -->
          <div class="atp2-filter-card">
            <div class="atp2-or-grid">
              <div>
                <label class="atp2-label" for="atp2-type" data-help="procType">Procedure type</label>
                <multiselect
                  id="atp2-type"
                  v-model="draft.procTypes"
                  class="atp2-select"
                  :options="procedureTypes"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :show-labels="false"
                  :disabled="draft.procCodes.length > 0"
                  label="ItemName"
                  track-by="DefNum"
                  placeholder="Any type" />
              </div>
              <div class="atp2-or">OR</div>
              <div>
                <label class="atp2-label" for="atp2-code" data-help="procCode">Procedure code</label>
                <!--
                  atp2-select--code widens the OPTION LIST only, not the input.
                  vue-multiselect hard-sets .multiselect__content-wrapper to
                  width:100%, which pinned the list to this ~265px column and
                  ellipsised every procedure description. See the stylesheet.
                -->
                <multiselect
                  id="atp2-code"
                  v-model="draft.procCodes"
                  class="atp2-select atp2-select--code"
                  :options="procCodeOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :show-labels="false"
                  :disabled="draft.procTypes.length > 0"
                  :custom-label="codeWithDesc"
                  label="ProcCode"
                  track-by="ProcCode"
                  placeholder="Search code or description">
                  <template slot="tag" slot-scope="{ option, remove }">
                    <span class="multiselect__tag">{{ option.ProcCode }}
                      <i class="multiselect__tag-icon" @click.prevent="remove(option)" />
                    </span>
                  </template>
                  <template slot="option" slot-scope="{ option }">
                    <span class="atp2-code-option">
                      <span class="atp2-code-badge">{{ option.ProcCode }}</span>
                      <span class="atp2-code-option-desc">{{ option.Descript }}</span>
                    </span>
                  </template>
                </multiselect>
              </div>
            </div>
          </div>

          <!-- Providers + presenter -->
          <div class="atp2-filter-card">
            <div class="atp2-prov-grid">
              <div>
                <label class="atp2-label" for="atp2-prov" data-help="providers">Providers</label>
                <multiselect
                  id="atp2-prov"
                  class="atp2-select"
                  :value="draft.providers"
                  :options="providerGroups"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :show-labels="false"
                  :group-select="false"
                  group-values="items"
                  group-label="label"
                  :custom-label="providerLabel"
                  track-by="ProvNum"
                  placeholder="All providers"
                  @input="onProvidersInput" />
                <p class="atp2-hint">{{ providerHint }}</p>
              </div>
              <div>
                <label class="atp2-label" for="atp2-presenter" data-help="presenter">Presenter</label>
                <multiselect
                  id="atp2-presenter"
                  v-model="draft.presenter"
                  class="atp2-select"
                  :options="activeEmployees || []"
                  :multiple="false"
                  :show-labels="false"
                  :custom-label="employeeLabel"
                  track-by="EmployeeID"
                  placeholder="Anyone" />
              </div>
            </div>
          </div>

          <!-- Treatment priorities -->
          <div class="atp2-filter-card">
            <label class="atp2-label" for="atp2-priorities" data-help="priorities">Treatment priorities</label>
            <div style="display:flex;align-items:flex-start;gap:8px">
              <div class="atp2-seg" style="flex:none">
                <button
                  v-for="mode in ['include', 'exclude']"
                  :key="mode"
                  type="button"
                  :class="{ 'is-active': draft.priorityFilterType === mode }"
                  @click="draft.priorityFilterType = mode">
                  {{ mode === 'include' ? 'Include' : 'Exclude' }}
                </button>
              </div>
              <multiselect
                id="atp2-priorities"
                v-model="draft.priorities"
                class="atp2-select"
                style="flex:1"
                :options="txPriorities"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :show-labels="false"
                label="ItemName"
                track-by="DefNum"
                placeholder="Add priorities" />
            </div>
          </div>
        </div>

        <!-- Timeframe -->
        <div class="atp2-timeframe">
          <span class="atp2-timeframe-label" data-help="timeframe">Timeframe</span>
          <!--
            Disabled while a fetch is in flight: applyFilters() now refuses to
            re-enter, so a click here would move the chip's active state without
            moving the data behind it. Better to not accept the click.
          -->
          <button
            v-for="r in rangePresets"
            :key="r.key"
            type="button"
            class="atp2-chip"
            :class="{ 'is-active': draft.rangeKey === r.key }"
            :disabled="fetching"
            @click="setRange(r.key)">
            {{ r.label }}
          </button>

          <label class="atp2-date">
            <span>From</span>
            <input v-model="draft.from" type="date" :max="today" :min="minDate" @change="draft.rangeKey = 'custom'">
          </label>
          <label class="atp2-date">
            <span>To</span>
            <input v-model="draft.to" type="date" :max="today" :min="minDate" @change="draft.rangeKey = 'custom'">
          </label>

          <!--
            Apply FIRST, Reset second. Reset used to sit to the left of the primary
            button and got hit twice by someone reaching for Apply, wiping a
            hand-built filter set. The extra left margin on Reset keeps it out of
            the button's fat-finger radius.
          -->
          <div class="atp2-timeframe-actions">
            <button type="button" class="atp2-btn atp2-btn--primary" :disabled="fetching" @click="applyFilters">
              {{ fetching ? 'Loading…' : 'Apply filters' }}
            </button>
            <button type="button" class="atp2-btn atp2-btn--link atp2-btn--reset" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>

      <!-- ================================================================ -->
      <!-- KPI STRIP                                                        -->
      <!-- ================================================================ -->
      <div class="atp2-strip">
        <div class="atp2-strip-wrap">
          <div v-for="tile in stripTiles" :key="tile.label" class="atp2-tile">
            <div class="atp2-tile-label" :data-help="tile.help">{{ tile.label }}</div>
            <div style="display:flex;align-items:baseline;gap:9px;margin-top:6px">
              <span class="atp2-tile-value" :style="{ color: tile.color }">{{ tile.value }}</span>
              <span class="atp2-tile-sub">{{ tile.sub }}</span>
            </div>
          </div>

          <div class="atp2-funnel">
            <div>
              <div class="atp2-tile-label" data-help="acceptance">Tx acceptance</div>
              <div style="display:flex;align-items:baseline;gap:8px;margin-top:4px">
                <span class="atp2-acc-big" :style="{ color: accColor(totals.acc) }">{{ pct(totals.acc) }}</span>
                <span class="atp2-tile-sub" data-help="subsets">of presented {{ currencySymbol }}</span>
              </div>
            </div>

            <!--
              Sits beside the dollar figure ON PURPOSE. The gap between the two IS
              the insight: a practice presenting large phased plans scores badly on
              the left number while every patient is actually moving forward, which
              is the whole reason this metric exists. Neither replaces the other.
            -->
            <div class="atp2-patient-acc">
              <div class="atp2-tile-label" data-help="patientAcceptance">Patient acceptance</div>
              <div style="display:flex;align-items:baseline;gap:8px;margin-top:4px">
                <span class="atp2-acc-big" :style="{ color: accColor(patients.acc) }">{{ pct1(patients.acc) }}</span>
                <span class="atp2-tile-sub" data-help="recallExcluded">{{ patientAccSub }}</span>
              </div>
            </div>
            <div style="flex:1;min-width:150px">
              <div class="atp2-funnel-bar">
                <div :style="{ width: barWidth(totals.sched, totals.planned), background: 'var(--atp2-blue)' }" />
                <div :style="{ width: barWidth(totals.comp, totals.planned), background: 'var(--atp2-green)' }" />
              </div>
              <div class="atp2-funnel-legend">
                <span><i class="atp2-dot atp2-dot--round" style="background:var(--atp2-blue)" />Scheduled {{ compact(totals.sched) }}</span>
                <span><i class="atp2-dot atp2-dot--round" style="background:var(--atp2-green)" />Completed {{ compact(totals.comp) }}</span>
                <span><i class="atp2-dot atp2-dot--round" style="background:var(--atp2-iris-200)" />Still unscheduled {{ compact(totals.unsched) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================ -->
      <!-- CONTENT                                                          -->
      <!-- ================================================================ -->
      <div class="atp2-content">

        <div v-if="!visibleRows.length" class="atp2-card">
          <div class="atp2-empty">
            <div class="atp2-empty-title">No treatment planned in this window</div>
            <div class="atp2-empty-sub">Widen the timeframe, or clear the procedure and provider filters.</div>
          </div>
        </div>

        <template v-else>

          <!-- ---------------------------------------------------------- -->
          <!-- PROVIDER COMPARISON                                        -->
          <!-- ---------------------------------------------------------- -->
          <div v-if="comparison" class="atp2-card">
            <div class="atp2-card-head">
              <h2 data-help="codeMatrix">Comparing {{ comparison.cards.length }} providers</h2>
              <span class="atp2-card-sub">{{ comparison.subtitle }}</span>
              <div class="atp2-card-actions">
                <span v-for="c in comparison.cards" :key="`lg-${c.provNum}`" style="display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;color:var(--atp2-n600)">
                  <i class="atp2-dot" :style="{ background: c.color }" />{{ c.shortName }}
                </span>
              </div>
            </div>

            <div v-if="comparison.roleWarning" class="atp2-warn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5h.01" /></svg>
              {{ comparison.roleWarning }}
            </div>

            <div class="atp2-cmp-grid" :style="comparison.gridStyle">
              <div
                v-for="c in comparison.cards"
                :key="c.provNum"
                class="atp2-cmp-card"
                :class="{ 'is-dim': hoverProvider !== null && hoverProvider !== c.provNum }"
                :style="{ borderColor: c.borderColor }"
                @mouseenter="hoverProvider = c.provNum"
                @mouseleave="hoverProvider = null">
                <div class="atp2-cmp-top" :style="{ background: c.color }" />

                <div class="atp2-cmp-id">
                  <span class="atp2-avatar" :style="{ background: c.tint, color: c.color }">{{ c.initials }}</span>
                  <div style="min-width:0">
                    <div class="atp2-cmp-name">{{ c.name }}</div>
                    <div class="atp2-cmp-role">{{ c.role }}</div>
                  </div>
                  <button type="button" class="atp2-details-btn atp2-noprint" style="margin-left:auto" title="Patient-level details" @click="openDrill(c.provNum)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></svg>
                  </button>
                </div>

                <div class="atp2-cmp-body">
                  <div class="atp2-ring">
                    <div class="atp2-ring-fill" :style="{ background: ringGradient(c.stats.acc, c.color) }" />
                    <div class="atp2-ring-hole">
                      <span class="atp2-ring-value" :style="{ color: c.color }">{{ pct1(c.stats.acc) }}</span>
                      <span class="atp2-ring-label">Accepted</span>
                    </div>
                  </div>

                  <div class="atp2-metrics">
                    <div v-for="m in c.metrics" :key="m.label" class="atp2-metric">
                      <div class="atp2-metric-head">
                        <span class="atp2-metric-label">{{ m.label }}</span>
                        <span class="atp2-metric-value" :style="{ color: m.color }">{{ m.value }}</span>
                        <span class="atp2-metric-sub">{{ m.sub }}</span>
                      </div>
                      <div class="atp2-metric-track">
                        <div class="atp2-metric-bar" :style="{ width: barWidth(m.raw, comparison.maxPlanned), background: m.barColor }" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="c.codes.length" class="atp2-code-table">
                  <div class="atp2-code-head">
                    <span>Code</span><span style="text-align:right">Tx planned</span><span style="text-align:right">Acc</span>
                  </div>
                  <div v-for="code in c.visibleCodes" :key="code.code" class="atp2-code-row">
                    <span style="min-width:0;display:flex;align-items:center;gap:7px">
                      <span class="atp2-code-badge">{{ code.code }}</span>
                      <span class="atp2-code-desc">{{ code.desc }}</span>
                    </span>
                    <span style="text-align:right">
                      <span style="font-family:var(--atp2-font-display);font-weight:700;font-size:12.5px;font-variant-numeric:tabular-nums">{{ money(code.planned, 0) }}</span>
                      <span style="font-size:10.5px;color:var(--atp2-n400);margin-left:5px;font-variant-numeric:tabular-nums">{{ code.plannedC }}×</span>
                    </span>
                    <span style="text-align:right;font-size:12px;font-weight:700;font-variant-numeric:tabular-nums" :style="{ color: accColor(code.acc) }">{{ pct0(code.acc) }}</span>
                  </div>
                  <button v-if="c.codes.length > CODE_PREVIEW" type="button" class="atp2-more" @click="expandedCodes = !expandedCodes">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path :d="expandedCodes ? 'm18 15-6-6-6 6' : 'm6 9 6 6 6-6'" /></svg>
                    {{ expandedCodes ? 'Show fewer' : `+${c.codes.length - CODE_PREVIEW} more codes` }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ---------------------------------------------------------- -->
          <!-- BY PROVIDER                                                -->
          <!-- ---------------------------------------------------------- -->
          <div class="atp2-card">
            <div class="atp2-card-head">
              <h2>By provider</h2>
              <span class="atp2-card-sub">{{ providerCountLabel }} · one row per provider, every stage side by side</span>
              <div class="atp2-card-actions atp2-noprint">
                <span style="font-size:11.5px;color:var(--atp2-muted)">Sort</span>
                <button
                  v-for="s in sortOptions"
                  :key="s.key"
                  type="button"
                  class="atp2-chip"
                  style="height:26px;padding:0 9px;font-size:11.5px"
                  :class="{ 'is-active': sort === s.key }"
                  @click="sort = s.key">
                  {{ s.label }}
                </button>
              </div>
            </div>

            <!-- Desktop table -->
            <div v-if="!isMobile" class="atp2-scroll-x">
              <div class="atp2-table">
                <div class="atp2-thead">
                  <span data-help="providers">Provider</span>
                  <span style="text-align:right" data-help="presented">Tx planned {{ currencySymbol }}</span>
                  <span style="text-align:right" data-help="scheduled">Scheduled {{ currencySymbol }}</span>
                  <span style="text-align:right" data-help="completed">Completed {{ currencySymbol }}</span>
                  <span style="text-align:right" data-help="unscheduled">Unscheduled {{ currencySymbol }}</span>
                  <span data-help="acceptance">Acceptance</span>
                  <span style="text-align:center">Details</span>
                </div>

                <div v-for="p in providerRows" :key="p.provNum" class="atp2-trow">
                  <div style="display:flex;align-items:center;gap:10px;min-width:0">
                    <span class="atp2-avatar-sm">{{ p.initials }}</span>
                    <span style="font-size:13.5px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ p.provider }}</span>
                  </div>
                  <div>
                    <div class="atp2-num">{{ money(p.planned) }}</div>
                    <div class="atp2-num-sub">{{ p.plannedC }} procs</div>
                  </div>
                  <div>
                    <div class="atp2-num" style="color:var(--atp2-blue)">{{ p.sched ? money(p.sched) : '—' }}</div>
                    <div class="atp2-num-sub">{{ p.schedC ? `${p.schedC} procs` : '' }}</div>
                  </div>
                  <div>
                    <div class="atp2-num" style="color:var(--atp2-green)">{{ p.comp ? money(p.comp) : '—' }}</div>
                    <div class="atp2-num-sub">{{ p.compC ? `${p.compC} procs` : '' }}</div>
                  </div>
                  <div>
                    <div class="atp2-num" :style="{ color: p.unsched > 0 ? 'var(--atp2-amber-ink)' : 'var(--atp2-n300)' }">{{ money(p.unsched) }}</div>
                    <div class="atp2-num-sub">{{ p.unschedC }} procs</div>
                  </div>
                  <div style="display:flex;align-items:center;gap:9px">
                    <div class="atp2-acc-track">
                      <div style="height:100%;border-radius:6px" :style="{ width: barWidth(p.acc, 100), background: accColor(p.acc) }" />
                    </div>
                    <span class="atp2-acc-text" :style="{ color: accColor(p.acc) }">{{ pct1(p.acc) }}</span>
                  </div>
                  <div style="display:flex;justify-content:center" class="atp2-noprint">
                    <button type="button" class="atp2-details-btn" title="Patient-level details" @click="openDrill(p.provNum)">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></svg>
                    </button>
                  </div>
                </div>

                <div class="atp2-ttotal">
                  <span style="font-size:12.5px;letter-spacing:.02em">Practice total</span>
                  <span style="text-align:right">{{ money(totals.planned) }}</span>
                  <span style="text-align:right;color:var(--atp2-blue)">{{ money(totals.sched) }}</span>
                  <span style="text-align:right;color:var(--atp2-green)">{{ money(totals.comp) }}</span>
                  <span style="text-align:right;color:var(--atp2-amber-ink)">{{ money(totals.unsched) }}</span>
                  <span>{{ pct(totals.acc) }}</span>
                  <span />
                </div>
              </div>
            </div>

            <!-- Mobile cards -->
            <div v-else class="atp2-mob-list" style="padding:12px">
              <div v-for="p in providerRows" :key="p.provNum" class="atp2-mob-card">
                <div style="display:flex;align-items:center;gap:9px">
                  <span class="atp2-avatar-sm">{{ p.initials }}</span>
                  <span style="font-size:13.5px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ p.provider }}</span>
                  <button type="button" class="atp2-details-btn atp2-noprint" style="margin-left:auto;width:38px;height:38px" @click="openDrill(p.provNum)">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></svg>
                  </button>
                </div>
                <div style="display:flex;align-items:center;gap:9px;margin:11px 0 10px">
                  <div class="atp2-acc-track">
                    <div style="height:100%;border-radius:6px" :style="{ width: barWidth(p.acc, 100), background: accColor(p.acc) }" />
                  </div>
                  <span class="atp2-acc-text" :style="{ color: accColor(p.acc) }">{{ pct1(p.acc) }}</span>
                </div>
                <div class="atp2-mob-cells">
                  <div v-for="cell in mobileCells(p)" :key="cell.label">
                    <div class="atp2-mob-label">{{ cell.label }}</div>
                    <div class="atp2-mob-value" :style="{ color: cell.color }">{{ cell.value }}</div>
                    <div class="atp2-num-sub" style="text-align:left">{{ cell.sub }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ---------------------------------------------------------- -->
          <!-- WHO MOVED FORWARD                                          -->
          <!--                                                            -->
          <!-- The sub-report Paul Gilreath asked for: "show if a patient  -->
          <!-- accepted and scheduled A PORTION of the treatment". The     -->
          <!-- middle bucket is his case — invisible in the dollar metric, -->
          <!-- where a patient doing 2 crowns a month reads as 20% failed. -->
          <!-- ---------------------------------------------------------- -->
          <div v-if="movement" class="atp2-card">
            <div class="atp2-card-head">
              <h2 data-help="movedForward">Who moved forward</h2>
              <span class="atp2-card-sub">{{ movement.subtitle }}</span>
            </div>

            <div class="atp2-movement">
              <div class="atp2-movement-bar">
                <div
                  v-for="b in movement.buckets"
                  :key="`bar-${b.key}`"
                  :style="{ width: b.width, background: b.color }"
                  :title="`${b.label} — ${b.countLabel}`" />
              </div>

              <!--
                Buttons, not rows: each opens the drill scoped to exactly these
                patients, which is what turns "8 nothing scheduled" into a call
                list. A bucket with no patients is disabled rather than hidden —
                a missing row reads as a rendering bug, an empty one reads as good
                news.
              -->
              <button
                v-for="b in movement.buckets"
                :key="b.key"
                type="button"
                class="atp2-movement-row"
                :class="{ 'is-empty': !b.count }"
                :disabled="!b.count"
                @click="openBucketDrill(b)">
                <i class="atp2-dot atp2-dot--round" :style="{ background: b.color }" />
                <span class="atp2-movement-label">{{ b.label }}</span>
                <span class="atp2-movement-count">{{ b.countLabel }}</span>
                <span class="atp2-movement-money">{{ b.money }}</span>
                <span class="atp2-movement-pct">{{ b.count ? pct0(b.share) : '' }}</span>
                <svg
                  v-if="b.count"
                  class="atp2-noprint"
                  width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                <span v-else class="atp2-noprint" style="width:15px" />
              </button>

              <p v-if="movement.excludedNote" class="atp2-movement-note">{{ movement.excludedNote }}</p>
            </div>
          </div>

          <!-- ---------------------------------------------------------- -->
          <!-- WHERE THE MONEY SITS                                       -->
          <!-- ---------------------------------------------------------- -->
          <div class="atp2-card">
            <div class="atp2-card-head">
              <h2 data-help="category">Where the money sits</h2>
              <span class="atp2-card-sub">Production by procedure category — the same category is the same colour in all three stages</span>
            </div>
            <div class="atp2-donuts">
              <div v-for="d in donuts" :key="d.title" class="atp2-donut">
                <div class="atp2-donut-title">
                  <i class="atp2-dot atp2-dot--round" :style="{ background: d.dot }" />{{ d.title }}
                </div>
                <div v-if="!d.list.length" class="atp2-empty-sub">Nothing in this stage.</div>
                <div v-else class="atp2-donut-row">
                  <div class="atp2-donut-ring">
                    <div class="atp2-donut-fill" :style="{ background: d.gradient }" />
                    <div class="atp2-donut-hole">
                      <span class="atp2-donut-total">{{ compact(d.total) }}</span>
                      <span class="atp2-donut-count">{{ d.count }} procs</span>
                    </div>
                  </div>
                  <ul class="atp2-donut-legend">
                    <li v-for="c in d.list" :key="c.name">
                      <i class="atp2-dot" :style="{ background: categories.colorMap[c.name] }" />
                      <span class="atp2-legend-name">{{ c.name }}</span>
                      <span class="atp2-legend-amount">{{ money(c.amount, 0) }}</span>
                      <span class="atp2-legend-pct">{{ pct0(d.total ? (c.amount / d.total) * 100 : 0) }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- ---------------------------------------------------------- -->
          <!-- PER-YEAR TREND                                             -->
          <!-- ---------------------------------------------------------- -->
          <div v-for="y in yearBlocks" :key="y.year" class="atp2-card">
            <div class="atp2-year-head">
              <h3 data-help="monthly">{{ y.year }}</h3>
              <span style="font-size:11.5px;color:var(--atp2-muted)">{{ y.note }}</span>
              <div class="atp2-year-chips">
                <div>
                  <div class="atp2-year-chip-label">Presented</div>
                  <div class="atp2-year-chip-value">{{ money(y.totals.planned) }}</div>
                </div>
                <div>
                  <div class="atp2-year-chip-label">Accepted</div>
                  <div class="atp2-year-chip-value" style="color:var(--atp2-green)">{{ money(y.totals.accepted) }}</div>
                </div>
                <div>
                  <div class="atp2-year-chip-label">Acceptance</div>
                  <div class="atp2-year-chip-value" :style="{ color: accColor(y.totals.acc) }">{{ pct(y.totals.acc) }}</div>
                </div>
              </div>
            </div>

            <div v-if="!y.hasData" class="atp2-empty">
              <div class="atp2-empty-title">Nothing treatment planned in {{ y.year }}</div>
            </div>

            <template v-else>
              <div class="atp2-chart">
                <div class="atp2-axis">
                  <span v-for="(a, i) in y.axis" :key="`ax-${i}`">{{ a }}</span>
                </div>
                <div style="flex:1;min-width:0">
                  <div class="atp2-plot">
                    <div class="atp2-grid">
                      <span v-for="g in 5" :key="`g-${g}`" />
                    </div>
                    <div class="atp2-bars">
                      <div v-for="m in y.months" :key="`b-${m.monthIndex}`" class="atp2-bar-pair">
                        <div
                          class="atp2-bar atp2-bar--presented"
                          :title="`${m.label} presented ${money(m.planned)}`"
                          :style="{ height: barHeight(m.planned, y.top) }" />
                        <div
                          class="atp2-bar atp2-bar--accepted"
                          :title="`${m.label} accepted ${money(m.accepted)}`"
                          :style="{ height: barHeight(m.accepted, y.top) }" />
                      </div>
                    </div>
                  </div>
                  <div class="atp2-month-labels">
                    <span
                      v-for="m in y.months"
                      :key="`ml-${m.monthIndex}`"
                      class="atp2-month-label"
                      :class="{ 'has-data': m.hasData }">{{ m.label }}</span>
                  </div>
                </div>
              </div>

              <div class="atp2-chart-legend">
                <span><i class="atp2-dot" style="background:var(--atp2-iris-400)" />Presented (tx planned)</span>
                <span><i class="atp2-dot" style="background:var(--atp2-green)" />Accepted (scheduled or completed)</span>
              </div>

              <div v-if="!isMobile" class="atp2-scroll-x" style="border-top:1px solid var(--atp2-border)">
                <div class="atp2-month-table">
                  <div class="atp2-mt-head">
                    <span>Month</span>
                    <span v-for="m in y.months" :key="`h-${m.monthIndex}`">{{ m.label }}</span>
                    <span>Total</span>
                  </div>

                  <div class="atp2-mt-row">
                    <span class="atp2-mt-label">Presented (tx planned)</span>
                    <span
                      v-for="m in y.months"
                      :key="`p-${m.monthIndex}`"
                      class="atp2-mt-cell"
                      :style="{ color: m.planned ? 'var(--atp2-n800)' : 'var(--atp2-n300)' }">{{ m.planned ? money(m.planned) : '–' }}</span>
                    <span class="atp2-mt-total">{{ money(y.totals.planned) }}</span>
                  </div>

                  <div class="atp2-mt-row">
                    <span class="atp2-mt-label" data-help="accepted">Accepted</span>
                    <span
                      v-for="m in y.months"
                      :key="`a-${m.monthIndex}`"
                      class="atp2-mt-cell"
                      :style="{ color: m.accepted ? 'var(--atp2-green)' : 'var(--atp2-n300)' }">{{ m.accepted ? money(m.accepted) : '–' }}</span>
                    <span class="atp2-mt-total" style="color:var(--atp2-green)">{{ money(y.totals.accepted) }}</span>
                  </div>

                  <div class="atp2-mt-row is-rate">
                    <span class="atp2-mt-label" data-help="acceptance">Tx acceptance %</span>
                    <span
                      v-for="m in y.months"
                      :key="`r-${m.monthIndex}`"
                      class="atp2-mt-cell"
                      style="font-weight:700"
                      :style="rateCellStyle(m)">{{ m.planned ? pct1(m.acc) : '–' }}</span>
                    <span class="atp2-mt-total" :style="{ color: accColor(y.totals.acc) }">{{ pct(y.totals.acc) }}</span>
                  </div>
                </div>
              </div>

              <!-- Mobile: a per-month list instead of the 12-column table -->
              <div v-else style="border-top:1px solid var(--atp2-border)">
                <div v-for="m in y.months.filter(x => x.hasData)" :key="`mm-${m.monthIndex}`" class="atp2-mt-mob">
                  <span style="font-size:12.5px;font-weight:700">{{ m.label }}</span>
                  <span style="text-align:right;font-size:12px;font-variant-numeric:tabular-nums">{{ money(m.planned) }}</span>
                  <span style="text-align:right;font-size:12px;font-variant-numeric:tabular-nums;color:var(--atp2-green)">{{ m.accepted ? money(m.accepted) : '—' }}</span>
                  <span style="text-align:right;font-size:12px;font-weight:700;font-variant-numeric:tabular-nums" :style="{ color: accColor(m.acc) }">{{ pct0(m.acc) }}</span>
                </div>
              </div>
            </template>
          </div>

          <p class="atp2-footnote">
            Acceptance is measured against the dollar value of what was treatment planned in the selected window — a
            procedure counts as accepted once it is scheduled or completed. A broken appointment counts as unscheduled.
            Figures read live from Open Dental.
          </p>
        </template>
      </div>
    </template>

    <!-- ================================================================== -->
    <!-- DRILL DRAWER                                                       -->
    <!-- ================================================================== -->
    <AdvancedTxPlannedV2Drill
      v-if="drill"
      :rows="drillRows"
      :provider-name="drillProviderName"
      :heading="drillHeading"
      :subheading="drillSubheading"
      :range-label="rangeLabel"
      :currency-symbol="currencySymbol"
      :is-mobile="isMobile"
      :recall-codes="recallCodes"
      @close="drill = null"
      @open-patient="gotoPatientPage" />

    <!-- ================================================================== -->
    <!-- EXPLAIN TIP                                                        -->
    <!--                                                                    -->
    <!-- ONE shared popover for every [data-help] mark on the panel and in   -->
    <!-- the drawer. Positioned ABSOLUTE inside .atp2 rather than fixed, on  -->
    <!-- purpose: this panel renders inside vue2-slideout-panel's .slideout, -->
    <!-- which settles at `transform: translateX(0)`, and a transformed      -->
    <!-- ancestor becomes the containing block for position:fixed children.  -->
    <!-- A fixed tip would be offset by the panel's left edge. Absolute also -->
    <!-- keeps it anchored to its label while the panel scrolls.            -->
    <!-- ================================================================== -->
    <div
      v-if="tip"
      ref="tip"
      class="atp2-xp-tip atp2-noprint"
      :class="{ 'is-above': tip.above }"
      :style="tipStyle"
      @mouseenter="cancelHideTip"
      @mouseleave="scheduleHideTip">
      <div class="atp2-xp-tip-head">
        <span class="atp2-xp-tip-icon">i</span>
        <span class="atp2-xp-tip-title">{{ tip.entry.title }}</span>
        <button type="button" class="atp2-xp-tip-x" title="Close" @click="hideTip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <p class="atp2-xp-tip-body">{{ tip.entry.body }}</p>
      <div v-if="tip.entry.formula" class="atp2-xp-tip-formula">{{ tip.entry.formula }}</div>
      <p v-if="tip.entry.note" class="atp2-xp-tip-note">{{ tip.entry.note }}</p>

      <a :href="tip.href" target="_blank" rel="noopener" class="atp2-xp-tip-link">
        Read the full guide
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13M12.5 6l6 6-6 6" /></svg>
      </a>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment-timezone';
import { mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';   // matches how Campaigns' filter cards load it
import mixin from '../../../mixin.js';
// Explicit .vue extension: webpack resolves it either way, but Vite (which the
// vitest component suite runs on) does not add .vue to resolve.extensions.
import PopupErrorDisplay from '@/components/ErrorDisplay/PopupErrorDisplay.vue';
import AdvancedTxPlannedV2Skeleton from './AdvancedTxPlannedV2Skeleton.vue';
import AdvancedTxPlannedV2Drill from './AdvancedTxPlannedV2Drill.vue';
import {
  aggregate, byProvider, sortProviders, byCategory, codeBreakdown, monthlyByYear,
  filterRows, initials as toInitials, roleOf, accColor as toAccColor, toNumber,
  byPatient, patientAcceptance, recallCodeSet, ALL_OF_IT, PARTIAL, NOTHING
} from './advancedTxPlannedV2Math';
import { helpFor, helpHref } from './advancedTxPlannedV2Help';
import {
  QUICK_SETS_KEY, MAX_NAME_LENGTH,
  parseQuickSets, resolveSet, isSetActive, toggleSet, suggestionsFor,
  validateSet, buildSavePayload, makeSetId
} from './advancedTxPlannedV2QuickSets';

// Up to three providers compare cleanly side by side; a fourth makes the cards
// too narrow to read and the eye stops comparing. Matches the design's cap.
const MAX_COMPARE = 3;
const CODE_PREVIEW = 5;
const COMPARE_COLORS = ['#4a48b8', '#7c3aed', '#b45309'];
const MAX_RANGE_YEARS = 5;

// Per-user, per-browser. Nothing here is worth a settings row: it is a reading
// preference, not office configuration.
const XP_KEY = 'teamio_atp2_explain';
const XP_COACH_KEY = 'teamio_atp2_explain_coach';

const TIP_WIDTH = 322;
// Flip the tip above its label when the label is this close to the bottom of the
// viewport, so a downward tip is never clipped by the panel's own edge.
const TIP_FLIP_MARGIN = 260;
// Long enough for the pointer to travel from label to tip.
const TIP_HIDE_DELAY = 160;

/** localStorage that cannot throw. A private window or blocked site data must not break the panel. */
function readFlag(key) {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    return null;
  }
}

function writeFlag(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    /* Storage is a convenience here; losing it costs the user one click. */
  }
}

/**
 * Treatment Presented vs Accepted — V2.
 *
 * ⚠️ V1 (AdvancedTxPlanned.vue) is still live and is NOT touched by this file.
 * Both panels call the same endpoint and must report the same money; the parity
 * guarantee lives in cypress/node-tests/tx-presented-v2-math.test.js.
 *
 * ── How filtering is split ──────────────────────────────────────────────────
 * SERVER (a refetch): procedure codes, procedure types, presenter, priorities,
 *   timeframe. Leaving codes/types to the client would make a wide date range run
 *   the full query every time, against a 30s Lambda timeout.
 * CLIENT (instant, no refetch): providers. The comparison view switches between
 *   up to three providers and must not go back to the network to do it, and the
 *   rows already carry ProvNum.
 *
 * `applyFilters()` refetches only when a server-relevant parameter actually
 * changed, so changing just the provider selection is instant.
 */
export default {
  name: 'AdvancedTxPlannedV2',
  components: { Multiselect, PopupErrorDisplay, AdvancedTxPlannedV2Skeleton, AdvancedTxPlannedV2Drill },
  mixins: [mixin],
  props: ['d'],

  data() {
    const from = moment().startOf('month').format('YYYY-MM-DD');
    const to = moment().format('YYYY-MM-DD');
    const blank = {
      procTypes: [], procCodes: [], providers: [], presenter: null,
      priorities: [], priorityFilterType: 'include',
      from, to, rangeKey: 'mtd'
    };

    return {
      CODE_PREVIEW,
      MAX_NAME_LENGTH,
      busy: true,
      fetching: false,
      results: null,
      fetchedParams: null,
      txPriorities: [],

      // ---- Quick sets ----------------------------------------------------
      // `quickSets` is what is stored; `quickSetDraft` is the working copy the
      // manage popover edits, so Cancel really cancels and one Save is one write.
      quickSets: [],
      quickSetsCorrupt: false,
      quickSetsDropped: 0,
      quickSetPanelOpen: false,
      quickSetDraft: [],
      quickSetName: '',
      quickSetError: '',
      quickSetHint: '',
      quickSetSaving: false,
      quickSetReplaceCorrupt: false,

      // ---- Explain -------------------------------------------------------
      // Off unless this browser turned it on before. The panel stays clean by
      // default; nobody is opted into a screenful of `?` marks.
      explain: readFlag(XP_KEY) === '1',
      explainCoachDismissed: readFlag(XP_COACH_KEY) === '1',
      tip: null,

      draft: Object.assign({}, blank),
      applied: Object.assign({}, blank),

      sort: 'planned',
      expandedCodes: false,
      hoverProvider: null,
      /**
       * The drill has two entry points now:
       *   { mode: 'provider', provNum }            — the Details button (unchanged)
       *   { mode: 'bucket', key, label, patNums }  — a "Who moved forward" bucket
       * null means closed.
       */
      drill: null,
      printedAt: '',
      providerHint: 'Leave empty for every provider, or pick 2–3 to compare them side by side.',
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1600
    };
  },

  created() {
    this.init();
  },

  watch: {
    // The mixin can still be filling `settings` when this panel mounts, and the
    // quick sets live in there. Without this the chip row would sit empty until the
    // next open, which reads as "our sets are gone".
    settings() {
      if (!this.quickSetPanelOpen) this.loadQuickSets();
    }
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
    // Listeners rather than only wiring the button, so Ctrl+P produces the same
    // report as clicking Print.
    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
    // The manage popover is a plain <div>, so it never receives keydown itself.
    // Escape has to be caught at the document.
    document.addEventListener('keydown', this.onKeydown);
    // Capture phase: a label nested inside something that stops propagation must
    // still resolve. Delegating from `document` also means the drill drawer gets
    // help marks for free, without any wiring of its own.
    document.addEventListener('pointerover', this.onHelpOver, true);
    document.addEventListener('pointerout', this.onHelpOut, true);
    document.addEventListener('click', this.onHelpTap, true);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerover', this.onHelpOver, true);
    document.removeEventListener('pointerout', this.onHelpOut, true);
    document.removeEventListener('click', this.onHelpTap, true);
    this.cancelHideTip();
    // Closing the panel mid-print must never leave the rest of the app hidden.
    this.onAfterPrint();
  },

  computed: {
    // ---- Layout -----------------------------------------------------------
    isMobile() { return this.windowWidth < 760; },
    isTablet() { return this.windowWidth < 1120; },

    today() { return moment().format('YYYY-MM-DD'); },
    minDate() { return moment().subtract(this.yearsOfDataToKeep, 'years').format('YYYY-MM-DD'); },

    /**
     * How far back this site keeps Open Dental data. The query floors ProcDate on
     * it, so asking for anything older silently returns nothing.
     */
    yearsOfDataToKeep() {
      const s = this.allSettings && this.allSettings.GENERAL_YEARS_OF_OD_DATA_TO_KEEP;
      return s ? parseInt(s.PropertyValue, 10) : 4;
    },

    currencySymbol() {
      const s = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      return (s && s.PropertyValue) || '$';
    },

    // ---- Raw result sets (positional — see the query's four statements) ----
    dataSets() {
      return (this.results && this.results.data) || [];
    },
    rawRows() { return this.dataSets[0] || []; },
    procedureTypes() { return this.dataSets[1] || []; },
    procCodeOptions() { return this.dataSets[2] || []; },
    providerOptions() { return this.dataSets[3] || []; },

    /**
     * Result set 4 — this office's OpenDental recall procedure codes.
     *
     * APPENDED LAST in the query on purpose: V1 reads sets 0–3 positionally and
     * ignores anything after, which is what makes the addition safe.
     *
     * Absent (a huddle-service that predates it) degrades to an empty set, and
     * `isCountableTreatment` then falls back to fee-bearing-only — the behaviour
     * from before recall exclusion existed. A frontend deployed ahead of the
     * Lambda gets a slightly generous number, never a crash.
     */
    recallCodes() { return recallCodeSet(this.dataSets[4] || []); },

    /** ProvNum → provider record, for role lookups on aggregated rows. */
    providersByNum() {
      const map = {};
      this.providerOptions.forEach((p) => { map[Number(p.ProvNum)] = p; });
      return map;
    },

    /** Doctors / Hygienists / Other, for the grouped picker. */
    providerGroups() {
      const buckets = { Doctor: [], Hygienist: [], Other: [] };
      this.providerOptions.forEach((p) => { buckets[roleOf(p.IsSecondary)].push(p); });
      return [
        { label: 'Doctors', items: buckets.Doctor },
        { label: 'Hygienists', items: buckets.Hygienist },
        { label: 'Other', items: buckets.Other }
      ].filter(g => g.items.length);
    },

    // ---- Derived views ----------------------------------------------------
    /**
     * Provider narrowing happens here, on ProvNum. Everything below — the KPI
     * strip, the table, the donuts, the trend — reads from this, so selecting
     * providers filters the whole panel consistently.
     */
    visibleRows() {
      const nums = this.applied.providers.map(p => p.ProvNum);
      return nums.length ? filterRows(this.rawRows, { provNums: nums }) : this.rawRows;
    },

    totals() { return aggregate(this.visibleRows); },

    providerRows() {
      const rolled = byProvider(this.visibleRows).map(p => Object.assign({}, p, {
        initials: toInitials(p.provider)
      }));
      return sortProviders(rolled, this.sort);
    },

    providerCountLabel() {
      const n = this.providerRows.length;
      return n === 1 ? '1 provider' : `${n} providers`;
    },

    categories() { return byCategory(this.visibleRows); },

    // ---- Patient-level acceptance -----------------------------------------
    /**
     * Counts PATIENTS rather than dollars. Reads from `visibleRows` like every
     * other derived view, so it respects the provider, code, type, priority and
     * date filters — which is what makes "% of patients who scheduled at least one
     * RESTORATIVE procedure" fall out of applying a Quick set.
     */
    patients() { return patientAcceptance(this.visibleRows, this.recallCodes); },

    patientRows() { return byPatient(this.visibleRows, this.recallCodes); },

    patientAccSub() {
      const p = this.patients;
      if (!p.patients) return 'nobody was presented treatment';
      return `${p.started} of ${p.patients} started treatment`;
    },

    /**
     * The three buckets, with the drill payload each one opens.
     *
     * Percentages are of `patients` (the three buckets), NOT of every patient in
     * the rows — the excluded, fee-less ones are called out separately in
     * `excludedNote` rather than being quietly folded into a denominator nobody
     * can see.
     */
    movement() {
      const p = this.patients;
      if (!p.patients && !p.excluded) return null;

      const byBucket = { all: [], partial: [], none: [] };
      this.patientRows.forEach((r) => {
        if (byBucket[r.bucket]) byBucket[r.bucket].push(r.patNum);
      });

      const share = n => (p.patients ? (n / p.patients) * 100 : 0);
      const count = n => `${n} ${n === 1 ? 'patient' : 'patients'}`;

      const buckets = [
        {
          key: ALL_OF_IT,
          label: 'Scheduled all of it',
          color: 'var(--atp2-green)',
          count: p.buckets.all,
          money: `${this.money(p.money.allPlanned, 0)} planned`,
          patNums: byBucket.all
        },
        {
          key: PARTIAL,
          label: 'Started, not finished',
          color: 'var(--atp2-blue)',
          count: p.buckets.partial,
          // THE number this feature exists to show. A patient doing 2 crowns a
          // month is scored as a 20% failure by the dollar metric; this says how
          // much of that "failure" is already on the schedule.
          money: `${this.money(p.money.partialAccepted, 0)} scheduled of ${this.money(p.money.partialPlanned, 0)}`,
          patNums: byBucket.partial
        },
        {
          key: NOTHING,
          label: 'Nothing scheduled',
          color: 'var(--atp2-amber-ink)',
          count: p.buckets.none,
          money: `${this.money(p.money.nonePlanned, 0)} still unscheduled`,
          patNums: byBucket.none
        }
      ].map(b => Object.assign(b, {
        countLabel: count(b.count),
        share: share(b.count),
        width: `${share(b.count).toFixed(2)}%`
      }));

      return {
        buckets,
        subtitle: p.patients
          ? `${count(p.patients)} presented treatment · ${this.pct1(p.acc)} scheduled at least one${this.recallCodes.size ? ' — recall visits do not count' : ''}`
          : 'Nobody was presented treatment to accept in this window',
        // Says WHY, which is the difference between a number people trust and a
        // number they argue with. Recall-only is called out by name because it is
        // the non-obvious one — those patients WERE seen, they just weren't
        // presented any treatment to accept.
        excludedNote: p.excluded
          ? (p.excludedRecallOnly
            ? `${count(p.excluded)} excluded (${p.excludedRecallOnly} presented recall work only) — nothing on their plan was treatment to accept.`
            : `${count(p.excluded)} excluded — every procedure on their plan carries no fee, so they can never count as having started.`)
          : ''
      };
    },

    donuts() {
      const c = this.categories;
      return [
        { title: 'Tx planned', dot: 'var(--atp2-iris-500)', list: c.planned, total: this.totals.planned, count: this.totals.plannedC },
        { title: 'Scheduled', dot: 'var(--atp2-blue)', list: c.sched, total: this.totals.sched, count: this.totals.schedC },
        { title: 'Completed', dot: 'var(--atp2-green)', list: c.comp, total: this.totals.comp, count: this.totals.compC }
      ].map(d => Object.assign(d, { gradient: this.donutGradient(d.list, d.total) }));
    },

    stripTiles() {
      const t = this.totals;
      // `help` keys the Explain popover for each tile — see advancedTxPlannedV2Help.js.
      return [
        { label: 'Tx planned', help: 'presented', value: this.money(t.planned), sub: `${t.plannedC.toLocaleString('en-US')} procs`, color: '' },
        { label: 'Scheduled', help: 'scheduled', value: this.money(t.sched), sub: `${t.schedC.toLocaleString('en-US')} procs`, color: 'var(--atp2-blue)' },
        { label: 'Completed', help: 'completed', value: this.money(t.comp), sub: `${t.compC.toLocaleString('en-US')} procs`, color: 'var(--atp2-green)' },
        { label: 'Unscheduled', help: 'unscheduled', value: this.money(t.unsched), sub: `${t.unschedC.toLocaleString('en-US')} procs`, color: 'var(--atp2-amber-ink)' }
      ];
    },

    /** Null unless 2–3 providers are selected — one provider has nothing to compare to. */
    comparison() {
      const picked = this.applied.providers;
      if (picked.length < 2) return null;

      const cards = picked.map((prov, i) => {
        const rows = filterRows(this.rawRows, { provNums: [prov.ProvNum] });
        const stats = aggregate(rows);
        const color = COMPARE_COLORS[i % COMPARE_COLORS.length];
        const codes = codeBreakdown(rows);
        const name = this.providerLabel(prov);

        return {
          provNum: prov.ProvNum,
          name,
          shortName: String(prov.LName || name).split(',')[0],
          role: roleOf(prov.IsSecondary),
          initials: toInitials(`${prov.LName}, ${prov.FName}`),
          color,
          tint: this.hexA(color, 0.12),
          borderColor: this.hexA(color, 0.28),
          stats,
          codes,
          visibleCodes: this.expandedCodes ? codes : codes.slice(0, CODE_PREVIEW),
          metrics: [
            { label: 'Tx planned', raw: stats.planned, value: this.money(stats.planned, 0), sub: `${stats.plannedC} procs`, color: '', barColor: color },
            { label: 'Scheduled', raw: stats.sched, value: this.money(stats.sched, 0), sub: `${stats.schedC} procs`, color: 'var(--atp2-blue)', barColor: '#3b82f6' },
            { label: 'Completed', raw: stats.comp, value: this.money(stats.comp, 0), sub: `${stats.compC} procs`, color: 'var(--atp2-green)', barColor: '#12805c' },
            { label: 'Unscheduled', raw: stats.unsched, value: this.money(stats.unsched, 0), sub: `${stats.unschedC} procs`, color: 'var(--atp2-amber-ink)', barColor: '#d19a3c' }
          ]
        };
      });

      const roles = [...new Set(cards.map(c => c.role))];
      const codeCount = this.applied.procCodes.length;

      return {
        cards,
        maxPlanned: Math.max(1, ...cards.map(c => c.stats.planned)),
        subtitle: [
          roles.length === 1 ? `${roles[0].toLowerCase()}s` : 'mixed roles',
          this.rangeLabel,
          codeCount ? (codeCount === 1 ? `1 procedure code (${this.applied.procCodes[0].ProcCode})` : `${codeCount} procedure codes`) : 'no code filter — presented vs accepted only'
        ].join(' · '),
        roleWarning: roles.length > 1
          ? 'Mixed roles in this comparison — hygiene and doctor production are not like-for-like.'
          : null,
        gridStyle: this.isMobile
          ? { gridTemplateColumns: 'minmax(0,1fr)' }
          : this.isTablet
            ? { gridAutoFlow: 'column', gridAutoColumns: 'minmax(292px,1fr)', overflowX: 'auto' }
            : { gridTemplateColumns: `repeat(${cards.length}, minmax(0,1fr))` }
      };
    },

    /**
     * Per-year trend, with the y-axis rounded up to a readable top value so the
     * gridlines land on round numbers rather than on the tallest bar.
     */
    yearBlocks() {
      return monthlyByYear(this.visibleRows, this.applied.from, this.applied.to).map((y) => {
        const max = Math.max(1, ...y.months.map(m => Math.max(m.planned, m.accepted)));
        const step = Math.pow(10, Math.floor(Math.log10(max)));
        const top = Math.ceil(max / step) * step;
        return Object.assign({}, y, {
          top,
          axis: [1, 0.75, 0.5, 0.25, 0].map(f => (f === 0 ? '0' : this.compact(top * f))),
          note: this.yearNote(y.year)
        });
      });
    },

    // ---- Drill ------------------------------------------------------------
    /**
     * Provider mode reads from `rawRows` (not `visibleRows`) exactly as before —
     * the Details button already names its own provider, so the panel-level
     * provider selection must not narrow it a second time.
     *
     * Bucket mode reads from `visibleRows`, because the buckets were computed from
     * `visibleRows`. Using rawRows there would show a patient procedures from a
     * provider the report is currently filtered away from, and the drawer's counts
     * would stop matching the card that opened it.
     */
    drillRows() {
      if (!this.drill) return [];
      if (this.drill.mode === 'bucket') {
        return filterRows(this.visibleRows, { patNums: this.drill.patNums });
      }
      return filterRows(this.rawRows, { provNums: [this.drill.provNum] });
    },

    drillProviderName() {
      if (!this.drill || this.drill.mode !== 'provider') return '';
      const p = this.providersByNum[Number(this.drill.provNum)];
      return p ? this.providerLabel(p) : '';
    },

    /** Blank in provider mode, so the drill falls back to its own providerName title. */
    drillHeading() {
      if (!this.drill || this.drill.mode !== 'bucket') return '';
      return this.drill.label;
    },

    drillSubheading() {
      if (!this.drill || this.drill.mode !== 'bucket') return '';
      const n = this.drill.patNums.length;
      return `${n} ${n === 1 ? 'patient' : 'patients'} · treatment planned ${this.rangeLabel} · click a patient to open their chart`;
    },

    // ---- Explain ----------------------------------------------------------
    showExplainCoach() { return !this.explainCoachDismissed; },

    /**
     * Absolute placement inside .atp2, computed in showTip() as a delta against
     * the root's own rect — so it is immune to the slideout's transform and stays
     * anchored to its label while the panel scrolls.
     */
    tipStyle() {
      if (!this.tip) return null;
      if (this.isMobile) return null;   // the stylesheet pins it as a bottom sheet
      return this.tip.above
        ? { left: `${this.tip.left}px`, bottom: `${this.tip.bottom}px` }
        : { left: `${this.tip.left}px`, top: `${this.tip.top}px` };
    },

    // ---- Misc -------------------------------------------------------------
    rangeLabel() {
      return `${this.formatDate(this.applied.from)} – ${this.formatDate(this.applied.to)}`;
    },

    rangePresets() {
      return [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'lastweek', label: 'Last week' },
        { key: 'lastmonth', label: 'Last month' },
        { key: 'mtd', label: 'Month to date' },
        { key: 'lastyear', label: 'Last year' },
        { key: 'custom', label: 'Custom' }
      ];
    },

    sortOptions() {
      return [
        { key: 'planned', label: this.currencySymbol },
        { key: 'acceptance', label: 'Acceptance' },
        { key: 'name', label: 'A–Z' }
      ];
    },

    // ---- Print-only filter summary ---------------------------------------
    // These read from `applied`, never `draft`: a PDF must describe the numbers
    // actually on the page, not what someone had half-typed into the filters.
    printProviders() {
      const picked = this.applied.providers;
      return picked.length ? picked.map(this.providerLabel).join(' · ') : 'All providers';
    },

    printProcedures() {
      if (this.applied.procCodes.length) {
        return `${this.applied.procCodes.length} code(s): ${this.applied.procCodes.map(c => c.ProcCode).join(', ')}`;
      }
      if (this.applied.procTypes.length) {
        return `${this.applied.procTypes.length} type(s): ${this.applied.procTypes.map(t => t.ItemName).join(', ')}`;
      }
      return 'All procedures';
    },

    printPriorities() {
      const picked = this.applied.priorities;
      if (!picked.length) return 'All priorities';
      const verb = this.applied.priorityFilterType === 'exclude' ? 'Excluding' : 'Only';
      return `${verb} ${picked.map(p => p.ItemName).join(', ')}`;
    },

    // ---- Quick sets -------------------------------------------------------
    /**
     * Quick sets are SITE-WIDE and shared, so creating and deleting them is an
     * admin act — otherwise one person's tidy-up silently removes everyone else's
     * chips, with nothing to point at afterwards. Everybody can still USE them.
     */
    canManageQuickSets() {
      return this.isSuperAdmin || !!(this.user && this.user.Role === 'Admin');
    },

    /**
     * Starter chips, shown ONLY to an office that has none of its own.
     *
     * Not shown when the stored blob is corrupt: the office DOES have sets, we
     * just could not read them, and replacing them on screen with three
     * suggestions would read as "yours are gone, here are some others".
     */
    quickSetSuggestions() {
      if (this.quickSets.length || this.quickSetsCorrupt) return [];
      return suggestionsFor(this.procCodeOptions);
    },

    /**
     * What the chip row renders. Codes are resolved against THIS site's live
     * procedurecode list every time, so a code retired in Open Dental shows up as
     * missing instead of quietly shrinking the set.
     */
    quickSetChips() {
      const source = this.quickSets.length ? this.quickSets : this.quickSetSuggestions;

      return source.map((set) => {
        const resolved = resolveSet(set, this.procCodeOptions);
        const notes = [];
        if (set.suggested) notes.push('Suggested — click to use, or save your own.');
        if (resolved.options.length) notes.push(resolved.options.map(o => o.ProcCode).join(', '));
        if (resolved.missing.length) notes.push(`No longer in Open Dental: ${resolved.missing.join(', ')}`);

        return {
          id: set.id,
          name: set.name,
          suggested: !!set.suggested,
          options: resolved.options,
          missing: resolved.missing,
          count: resolved.options.length,
          active: isSetActive(this.draft.procCodes, resolved.options),
          disabled: resolved.options.length === 0,
          title: notes.join('\n')
        };
      });
    },

    /** Bounded — 40 codes spelled out would push the popover's buttons off screen. */
    selectedCodeList() {
      const codes = this.draft.procCodes.map(c => c.ProcCode);
      if (codes.length <= 8) return codes.join(', ');
      return `${codes.slice(0, 8).join(', ')} +${codes.length - 8} more`;
    },

    /** Nothing to save means the Save button stays off — including a pointless empty write. */
    quickSetDirty() {
      const shrink = list => JSON.stringify((list || []).map(s => ({
        name: String(s.name || '').trim(),
        codes: (s.codes || []).slice()
      })));
      return shrink(this.quickSetDraft) !== shrink(this.quickSets);
    }
  },

  methods: {
    ...mapActions('huddle', ['getHuddleAdvancedTxPlannedData', 'getTxPriorities']),

    async init() {
      this.busy = true;
      this.loadQuickSets();
      try {
        this.txPriorities = await this.getTxPriorities({ siteID: this.siteID });
      } catch (err) {
        // Priorities are an optional filter; losing them must not blank the panel.
        console.log('V2: could not load tx priorities', err);
        this.txPriorities = [];
      }

      try {
        await this.fetch(this.serverParams(this.draft));
        this.applied = this.snapshot(this.draft);
      } catch (err) {
        this.$toast.error(err.message);
      } finally {
        this.busy = false;
      }
    },

    /** The parameters the SQL actually depends on. Providers are absent by design. */
    serverParams(f) {
      return {
        siteID: this.siteID,
        procType: f.procTypes.map(t => t.DefNum).join(','),
        procCode: f.procCodes.map(c => c.ProcCode).join(','),
        presenter: f.presenter ? f.presenter.ODUserNum : '',
        provNum: '',
        timeFrom: f.from,
        timeTo: f.to,
        priorities: f.priorities.map(p => p.DefNum).join(','),
        priorityFilterType: f.priorityFilterType,
        yearsOfDataToKeep: this.yearsOfDataToKeep,
        country: localStorage.getItem('country') || 'USA'
      };
    },

    async fetch(params) {
      this.fetching = true;
      try {
        this.results = await this.getHuddleAdvancedTxPlannedData(params);
        this.fetchedParams = JSON.stringify(params);
      } finally {
        this.fetching = false;
      }
    },

    snapshot(f) {
      return {
        procTypes: f.procTypes.slice(),
        procCodes: f.procCodes.slice(),
        providers: f.providers.slice(),
        presenter: f.presenter,
        priorities: f.priorities.slice(),
        priorityFilterType: f.priorityFilterType,
        from: f.from,
        to: f.to,
        rangeKey: f.rangeKey
      };
    },

    async applyFilters() {
      // Timeframe chips and quick-set chips both auto-apply, so two fast clicks
      // used to start overlapping requests with no sequencing — whichever RESPONSE
      // landed last won, which is not necessarily the last thing clicked.
      if (this.fetching) return;
      if (!this.validate()) return;

      const params = this.serverParams(this.draft);
      try {
        // Only go to the network when the SQL would actually change. Adjusting the
        // provider selection re-derives from the rows already in memory.
        if (JSON.stringify(params) !== this.fetchedParams) {
          await this.fetch(params);
        }
        this.applied = this.snapshot(this.draft);
        this.drill = null;
        this.expandedCodes = false;
      } catch (err) {
        this.$toast.error(err.message);
      }
    },

    /**
     * V1's validation ladder, kept intact. Every message here corresponds to a
     * way the query silently returns nothing or takes far too long.
     */
    validate() {
      if (this.draft.procTypes.length && this.draft.procCodes.length) {
        this.$toast.error('You can not filter by Procedure Type AND Procedure Code. Please use only one of them.');
        return false;
      }

      if (!this.draft.from || !this.draft.to) {
        this.$toast.error('Please select both start and end dates for the timeframe.');
        return false;
      }

      const start = moment(this.draft.from, 'YYYY-MM-DD');
      const end = moment(this.draft.to, 'YYYY-MM-DD');
      const today = moment().endOf('day');

      if (!start.isValid() || !end.isValid()) {
        this.$toast.error('Please select valid dates for the timeframe.');
        return false;
      }
      if (start.isAfter(end)) {
        this.$toast.error('Start date cannot be after end date. Please select a valid date range.');
        return false;
      }
      if (start.isAfter(today) || end.isAfter(today)) {
        this.$toast.error('Cannot select future dates. Please select dates up to today only.');
        return false;
      }
      if (end.diff(start, 'years', true) > MAX_RANGE_YEARS) {
        this.$toast.error(`Date range cannot exceed ${MAX_RANGE_YEARS} years. Please select a smaller range.`);
        return false;
      }
      if (start.isBefore(moment().subtract(this.yearsOfDataToKeep, 'years'))) {
        this.$toast.error(`Start date cannot be more than ${this.yearsOfDataToKeep} years ago. Please select a more recent date range.`);
        return false;
      }
      return true;
    },

    setRange(key) {
      const map = {
        today: [moment().startOf('day'), moment().endOf('day')],
        yesterday: [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')],
        lastweek: [moment().subtract(1, 'weeks').startOf('week'), moment().subtract(1, 'weeks').endOf('week')],
        lastmonth: [moment().subtract(1, 'months').startOf('month'), moment().subtract(1, 'months').endOf('month')],
        mtd: [moment().startOf('month'), moment()],
        lastyear: [moment().subtract(1, 'years').startOf('year'), moment().subtract(1, 'years').endOf('year')]
      };

      this.draft.rangeKey = key;
      if (!map[key]) return;   // 'custom' just unlocks the two date inputs

      this.draft.from = map[key][0].format('YYYY-MM-DD');
      this.draft.to = map[key][1].format('YYYY-MM-DD');
      this.applyFilters();
    },

    resetFilters() {
      this.draft.procTypes = [];
      this.draft.procCodes = [];
      this.draft.providers = [];
      this.draft.presenter = null;
      this.draft.priorities = [];
      this.draft.priorityFilterType = 'include';
      this.providerHint = 'Leave empty for every provider, or pick 2–3 to compare them side by side.';
      this.quickSetHint = '';
      this.setRange('mtd');
    },

    /**
     * vue-multiselect appends each new pick to the end of the array, so index 0 is
     * the oldest selection. Past the cap we drop that one rather than refusing the
     * click — refusing leaves people wondering why the checkbox did nothing.
     */
    onProvidersInput(list) {
      if (list.length > MAX_COMPARE) {
        const evicted = list[0];
        this.draft.providers = list.slice(-MAX_COMPARE);
        this.providerHint = `Max ${MAX_COMPARE} at a time — swapped out ${this.providerLabel(evicted)}.`;
        return;
      }

      this.draft.providers = list;
      if (!list.length) {
        this.providerHint = 'Leave empty for every provider, or pick 2–3 to compare them side by side.';
      } else if (list.length === 1) {
        this.providerHint = 'Add a second provider to turn on the side-by-side comparison.';
      } else if (list.length >= MAX_COMPARE) {
        this.providerHint = `${MAX_COMPARE} is the max — a fourth pick swaps out the oldest.`;
      } else {
        this.providerHint = `Comparing ${list.length}. Add one more, or hit Apply filters.`;
      }
    },

    // ---- Quick sets -------------------------------------------------------
    /** Re-read the stored blob. Cheap, and idempotent — safe to call after any save. */
    loadQuickSets() {
      const row = this.allSettings && this.allSettings[QUICK_SETS_KEY];
      const parsed = parseQuickSets(row ? row.PropertyValue : null);
      this.quickSets = parsed.sets;
      this.quickSetsCorrupt = parsed.corrupt;
      this.quickSetsDropped = parsed.dropped;
      if (parsed.dropped) {
        console.log(`V2 quick sets: skipped ${parsed.dropped} unusable entr(y/ies)`);
      }
    },

    /**
     * Chip click: ADD this set's codes to the selection, or take exactly them back
     * out if they are all already there. Two sets therefore give their union, and
     * a mis-click is undone by clicking again — neither of which a replace-on-click
     * preset can do.
     *
     * Auto-applies, matching the Timeframe chips. `applyFilters` refuses to
     * re-enter while a fetch is in flight, so hammering chips cannot race.
     */
    onQuickSetClick(chip) {
      if (this.fetching || chip.disabled) return;

      const notes = [];

      // Codes and types are mutually exclusive — `validate()` rejects both, and each
      // picker disables the other. Silently doing nothing here would look broken, so
      // the type filter goes and we say so.
      if (this.draft.procTypes.length) {
        this.draft.procTypes = [];
        notes.push('Cleared the procedure-type filter — codes and types cannot be combined.');
      }

      if (chip.missing.length) {
        notes.push(`${chip.missing.join(', ')} ${chip.missing.length === 1 ? 'is' : 'are'} no longer in Open Dental and ${chip.missing.length === 1 ? 'was' : 'were'} skipped.`);
      }

      this.draft.procCodes = toggleSet(this.draft.procCodes, chip.options);
      this.quickSetHint = notes.join(' ');
      this.applyFilters();
    },

    /**
     * Open the manage popover on a WORKING COPY. An office with no sets of its own
     * starts from the suggestions, so adopting all three is one click — the blob is
     * still only written when they press Save.
     */
    openQuickSetPanel(focusName) {
      const source = this.quickSets.length ? this.quickSets : this.quickSetSuggestions;
      this.quickSetDraft = source.map(s => ({ id: s.id, name: s.name, codes: s.codes.slice() }));
      this.quickSetName = '';
      this.quickSetError = '';
      this.quickSetReplaceCorrupt = false;
      this.quickSetPanelOpen = true;

      if (focusName) {
        this.$nextTick(() => { if (this.$refs.quickSetName) this.$refs.quickSetName.focus(); });
      }
    },

    closeQuickSetPanel() {
      this.quickSetPanelOpen = false;
      this.quickSetError = '';
    },

    /** Add the codes selected right now as a new row in the working copy. */
    addQuickSetFromSelection() {
      const check = validateSet(
        { name: this.quickSetName, codes: this.draft.procCodes },
        this.quickSetDraft
      );
      if (!check.ok) {
        this.quickSetError = check.errors[0];
        return;
      }

      this.quickSetDraft.push({
        id: makeSetId(Date.now(), this.quickSetDraft),
        name: check.name,
        codes: check.codes
      });
      this.quickSetName = '';
      this.quickSetError = check.rejected.length
        ? `Skipped ${check.rejected.join(', ')} — this report cannot filter on those.`
        : '';
    },

    /** Re-point an existing row at the current selection, without a second picker. */
    replaceQuickSetCodes(index) {
      const set = this.quickSetDraft[index];
      if (!set) return;

      const check = validateSet(
        { name: set.name, codes: this.draft.procCodes },
        this.quickSetDraft,
        { editingId: set.id }
      );
      if (!check.ok) {
        this.quickSetError = check.errors[0];
        return;
      }

      this.$set(this.quickSetDraft, index, Object.assign({}, set, { codes: check.codes }));
      this.quickSetError = '';
    },

    /**
     * Write the whole list in ONE upsert, then re-read so the panel reflects what
     * was actually stored rather than what we hoped.
     *
     * `buildSavePayload` owns every refusal — an unnamed or codeless set, a name the
     * latin1 column would mangle, a duplicate, an oversized blob, and (the dangerous
     * one) saving before settings loaded, which would delete every set we hadn't read.
     */
    async saveQuickSets() {
      const built = buildSavePayload(this.quickSetDraft, {
        siteId: this.siteID,
        settingsLoaded: !!(this.settings && this.settings.length),
        corrupt: this.quickSetsCorrupt,
        confirmedReplaceCorrupt: this.quickSetReplaceCorrupt
      });

      if (!built.ok) {
        this.quickSetError = built.errors[0];
        return;
      }

      this.quickSetSaving = true;
      try {
        await this.$store.dispatch('customer/upsertSettings', {
          settings: built.settings,
          // The site the payload was BUILT for, not a re-read — it cannot drift if
          // the active site changed while the popover was open.
          siteID: built.siteId
        });
        await this.$store.dispatch('customer/getSettings', { siteID: built.siteId });
        this.loadQuickSets();
        this.quickSetPanelOpen = false;
        this.quickSetError = '';
        this.$toast.success(built.sets.length ? 'Quick sets saved.' : 'Quick sets cleared.');
      } catch (err) {
        this.quickSetError = 'Could not save those quick sets. Please try again.';
        console.log('V2 quick sets save failed', err);
      } finally {
        this.quickSetSaving = false;
      }
    },

    openDrill(provNum) {
      this.drill = { mode: 'provider', provNum };
    },

    /**
     * Open the drill on one "Who moved forward" bucket — the patient set, not a
     * provider. This is what turns "8 patients, nothing scheduled" into a call
     * list.
     *
     * The patNums are SNAPSHOTTED here rather than re-derived inside the drawer:
     * the bucket the person clicked is the bucket they get, even though changing a
     * filter behind an open drawer would recompute `movement`.
     */
    openBucketDrill(bucket) {
      if (!bucket || !bucket.count) return;
      this.drill = {
        mode: 'bucket',
        key: bucket.key,
        label: bucket.label,
        patNums: bucket.patNums.slice()
      };
    },

    gotoPatientPage(patNum) {
      const teamioPatientID = `${this.siteID}-${patNum}`;
      const routeData = this.$router.resolve({ name: 'patientprofile', params: { patientID: teamioPatientID } });
      window.open(routeData.href, '_blank');
    },

    printReport() {
      // beforeprint does the real work — it fires for Ctrl+P too.
      window.print();
    },

    /**
     * Print ONLY this panel, in full.
     *
     * The panel is rendered inside vue2-slideout-panel's `.slideout`, which is
     * `position: fixed; height: 100%; overflow-y: auto`. A fixed, scrolling box
     * gives the browser exactly one viewport to lay out, so printing produced a
     * few mostly-empty sheets with the report sliced arbitrarily. The stylesheet
     * un-fixes that host — but it also has to hide the Huddle page behind the
     * panel, and "hide everything except this element" means walking UP the tree,
     * which CSS selectors cannot do.
     *
     * So we walk up once here, tagging every SIBLING of every ancestor, and let
     * one print-only rule (`.atp2-print-hide { display: none }`) do the rest.
     * Tagging siblings rather than using `visibility: hidden` matters: hidden
     * elements still occupy layout, which is what produces phantom blank pages.
     */
    onBeforePrint() {
      this.printedAt = moment().format('MM/DD/YYYY h:mm a');
      document.body.classList.add('atp2-printing');

      let node = this.$el;
      while (node && node.parentElement && node !== document.body) {
        const parent = node.parentElement;
        Array.prototype.forEach.call(parent.children, (sibling) => {
          if (sibling !== node) sibling.classList.add('atp2-print-hide');
        });
        node = parent;
      }
    },

    /** Must be idempotent — it also runs on unmount as a safety net. */
    onAfterPrint() {
      document.body.classList.remove('atp2-printing');
      const hidden = document.querySelectorAll('.atp2-print-hide');
      Array.prototype.forEach.call(hidden, el => el.classList.remove('atp2-print-hide'));
    },

    closeSlideOutPanel() {
      this.$emit('closePanel', { test: false });
    },

    onResize() {
      this.windowWidth = window.innerWidth;
    },

    onKeydown(e) {
      if (e.key !== 'Escape') return;
      if (this.tip) { this.hideTip(); return; }   // innermost thing first
      if (this.quickSetPanelOpen) this.closeQuickSetPanel();
    },

    // ---- Explain ----------------------------------------------------------
    toggleExplain() {
      this.explain = !this.explain;
      this.tip = null;
      writeFlag(XP_KEY, this.explain ? '1' : '0');
    },

    dismissExplainCoach() {
      this.explainCoachDismissed = true;
      writeFlag(XP_COACH_KEY, '1');
    },

    /** The nearest [data-help] ancestor of an event target, or null. */
    helpTargetOf(e) {
      const t = e && e.target;
      return t && t.closest ? t.closest('[data-help]') : null;
    },

    /**
     * ONE delegated set of listeners on `document`, rather than a component per
     * mark. That is what lets the drill drawer — a separate component rendered
     * outside this subtree — get help marks with no wiring of its own.
     *
     * Capture phase so a label inside something that stops propagation still
     * resolves.
     */
    onHelpOver(e) {
      if (!this.explain) return;
      const el = this.helpTargetOf(e);
      if (!el) return;
      const key = el.getAttribute('data-help');
      if (!helpFor(key)) return;

      this.cancelHideTip();
      if (this.tip && this.tip.el === el) return;
      this.showTip(key, el);
    },

    onHelpOut(e) {
      if (!this.tip) return;
      if (this.helpTargetOf(e) !== this.tip.el) return;
      // Moving deeper INTO the same label is not leaving it.
      const to = e.relatedTarget;
      if (to && this.tip.el.contains && this.tip.el.contains(to)) return;
      this.scheduleHideTip();
    },

    /** Touch: no hover, so a tap toggles. Also closes the tip on any outside tap. */
    onHelpTap(e) {
      if (e.target && e.target.closest && e.target.closest('.atp2-xp-tip')) return;

      const el = this.explain ? this.helpTargetOf(e) : null;
      if (!el) { if (this.tip) this.tip = null; return; }

      const key = el.getAttribute('data-help');
      if (!helpFor(key)) return;

      e.stopPropagation();
      if (this.tip && this.tip.el === el) this.tip = null;
      else this.showTip(key, el);
    },

    /**
     * Place the tip in .atp2's coordinate space.
     *
     * Deltas against the root's own bounding rect — NOT viewport coordinates with
     * position:fixed, which the slideout's transform would offset. `above` flips
     * the tip when the label sits low enough that a downward tip would be clipped.
     */
    showTip(key, el) {
      const entry = helpFor(key);
      const root = this.$refs.root;
      if (!entry || !root) return;

      this.cancelHideTip();

      const r = el.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const flip = r.bottom > (window.innerHeight - TIP_FLIP_MARGIN);

      // Clamped so a label at either edge cannot push the tip out of the panel.
      const half = TIP_WIDTH / 2;
      const centre = (r.left - rootRect.left) + (r.width / 2);
      const left = Math.min(Math.max(centre, half + 12), Math.max(half + 12, root.clientWidth - half - 12));

      this.tip = {
        key,
        el,
        entry,
        href: helpHref(key),
        above: flip,
        left,
        top: (r.bottom - rootRect.top) + 10,
        bottom: (rootRect.bottom - r.top) + 10
      };
    },

    hideTip() { this.cancelHideTip(); this.tip = null; },

    cancelHideTip() {
      if (this._tipTimer) { clearTimeout(this._tipTimer); this._tipTimer = null; }
    },

    /**
     * Delayed, so the pointer can travel from the label into the tip without it
     * vanishing en route — which is what makes the close button and the guide
     * link reachable at all.
     */
    scheduleHideTip() {
      this.cancelHideTip();
      this._tipTimer = setTimeout(() => { this.tip = null; }, TIP_HIDE_DELAY);
    },

    // ---- Formatting -------------------------------------------------------
    /**
     * Local money formatter. The global `money` filter hardcodes '$0.00' for a
     * zero value, so a non-US site would see the wrong symbol on every empty
     * cell — and this panel is full of them.
     */
    money(value, decimals) {
      const dp = decimals === undefined ? 2 : decimals;
      const n = toNumber(value);
      return this.currencySymbol + n.toFixed(dp).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    },

    compact(value) {
      const n = toNumber(value);
      if (Math.abs(n) >= 1000000) return `${this.currencySymbol}${(n / 1000000).toFixed(1)}M`;
      if (Math.abs(n) >= 1000) return `${this.currencySymbol}${Math.round(n / 1000)}k`;
      return `${this.currencySymbol}${Math.round(n)}`;
    },

    pct(v) { return `${toNumber(v).toFixed(2)}%`; },
    pct1(v) { return `${toNumber(v).toFixed(1)}%`; },
    pct0(v) { return `${toNumber(v).toFixed(0)}%`; },
    accColor(v) { return toAccColor(toNumber(v)); },

    formatDate(d) { return d ? moment(d, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''; },

    /** Width as a percentage, clamped so a rounding artefact can't overflow the track. */
    barWidth(value, of) {
      if (!of) return '0%';
      return `${Math.max(0, Math.min(100, (toNumber(value) / of) * 100)).toFixed(2)}%`;
    },

    /** Bars get a 1.5% floor so a small-but-real month is still visible. */
    barHeight(value, top) {
      const n = toNumber(value);
      if (!n || !top) return '0%';
      return `${Math.max(1.5, Math.min(100, (n / top) * 100)).toFixed(2)}%`;
    },

    ringGradient(pctValue, color) {
      const p = Math.max(0, Math.min(100, toNumber(pctValue))).toFixed(1);
      return `conic-gradient(${color} 0% ${p}%, var(--atp2-n100) ${p}% 100%)`;
    },

    donutGradient(list, total) {
      if (!list.length || !total) return 'var(--atp2-n100)';
      let at = 0;
      const stops = list.map((c) => {
        const from = (at / total) * 100;
        at += c.amount;
        const to = (at / total) * 100;
        return `${this.categories.colorMap[c.name]} ${from.toFixed(2)}% ${to.toFixed(2)}%`;
      });
      return `conic-gradient(${stops.join(',')})`;
    },

    rateCellStyle(m) {
      if (!m.planned) return { color: 'var(--atp2-n300)' };
      return {
        color: this.accColor(m.acc),
        background: m.acc >= 60 ? 'var(--atp2-green-50)' : m.acc >= 35 ? 'var(--atp2-amber-50)' : 'var(--atp2-red-50)'
      };
    },

    /** "Aug only" / "Jan – Aug in range" — which months of this year the range covers. */
    yearNote(year) {
      const start = moment(this.applied.from, 'YYYY-MM-DD');
      const end = moment(this.applied.to, 'YYYY-MM-DD');
      const first = start.year() === year ? start.format('MMM') : 'Jan';
      const last = end.year() === year ? end.format('MMM') : 'Dec';
      return first === last ? `${first} only` : `${first} – ${last} in range`;
    },

    mobileCells(p) {
      return [
        { label: 'Tx planned', value: this.money(p.planned, 0), sub: `${p.plannedC} procs`, color: '' },
        { label: 'Scheduled', value: p.sched ? this.money(p.sched, 0) : '—', sub: p.schedC ? `${p.schedC} procs` : '', color: 'var(--atp2-blue)' },
        { label: 'Completed', value: p.comp ? this.money(p.comp, 0) : '—', sub: p.compC ? `${p.compC} procs` : '', color: 'var(--atp2-green)' },
        { label: 'Unscheduled', value: this.money(p.unsched, 0), sub: `${p.unschedC} procs`, color: 'var(--atp2-amber-ink)' }
      ];
    },

    // ---- Option labels ----------------------------------------------------
    /**
     * Built as "LName, FName" to match the row's Provider column, which SQL builds
     * the same way — so the table, the comparison cards and the drill all agree on
     * how a provider is named.
     */
    providerLabel(p) {
      if (!p) return '';
      const suffix = p.Suffix ? ` ${p.Suffix}` : '';
      return `${p.LName}, ${p.FName}${suffix}`;
    },

    employeeLabel(e) { return e ? e.Name : ''; },
    codeWithDesc(c) { return `${c.ProcCode} — ${c.Descript}`; },

    hexA(hex, alpha) {
      const n = parseInt(hex.slice(1), 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    }
  }
};
</script>

<style src="./AdvancedTxPlannedV2.scss" lang="scss" />

<style lang="scss" scoped>
// Only the bits that don't belong in the shared token file live here.
.atp2-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  margin: 0;
  border: 1px solid var(--atp2-border-strong);
  border-radius: 10px;
  background: var(--atp2-surface);
  font-size: 13px;

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--atp2-muted);
  }

  input {
    border: 0;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    color: var(--atp2-text);
  }
}

.atp2-mt-mob {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1fr 1fr 54px;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--atp2-border);
  align-items: center;

  &:last-child { border-bottom: 0; }
}
</style>
